import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { fmtCurrency } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { feeOverlapWarning, invoiceForMoveJob, multipleInvoicesLabel, truckingInvoiceStatusSkin, type BolCoverage } from '../../lib/trucking-invoice-status';
import { useCreateInvoiceFromBol, useCreateInvoiceFromMoveJob, useTruckingInvoices } from '../../data/truckingData';
import type { TruckingInvoiceRefusalBody } from '../../types/trucking-invoices';

/**
 * The invoice for one BOL or one move job, on its office page (#815, api#954).
 *
 *   ready, invoiced   the invoice's number (opens it), status and total, as stored
 *   ready, none       a "No invoice" badge, and Generate Invoice for admin and
 *                     office staff (INVOICE_WRITE). It is how the office meets a
 *                     signature's `invoice_refusal`, which went to the driver's
 *                     phone and is not stored: Generate asks again, and the
 *                     API's refusal is shown as sent, each problem naming its BOL.
 *   not ready         one line: a BOL is invoiced once signed, a move job once
 *                     its master BOL is finalized (the API's own preconditions)
 *
 * A BOL's invoice is the API's `billed_on` (api#1011), read from the invoice
 * LINES and carried on the BOL itself: the number (opens it) and status, and
 * "Billed on N invoices" when the API counts more than one. A BOL read that
 * carries no coverage says nothing, never "No invoice".
 *
 * A move job's invoice is still found in GET /trucking/invoices (unpaginated;
 * the move job read carries no coverage), skipping a void one. "None" is said
 * only from a list that LOADED; a failed or refused read says so instead.
 *
 * `overlap` is the load's `rental_fee_overlap`, and on a move job `overlaps`
 * is each linked load's: when a rental mob / demob fee already bills a trip,
 * one line per trip says so. Nothing is blocked.
 *
 * Drivers see nothing here: the invoice routes refuse them.
 */
const READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'];
const WRITE_ROLES = ['admin', 'office_staff'];

export type InvoiceCardTarget =
  /**
   * `billedOnRental` (api#1054): the load's trip is billed on a rental invoice
   * as an hourly haul, so trucking does not invoice it (409
   * BOL_BILLED_ON_RENTAL). Generate is not offered.
   */
  | { kind: 'load'; id: string; ready: boolean; coverage: BolCoverage; billedOnRental?: boolean }
  | { kind: 'move_job'; id: string; ready: boolean };

/** A link to the invoice that bills a BOL. Shared with the BOL list, whose row click it must not trigger. */
export function BilledOnLink({ coverage, className = '' }: { coverage: BolCoverage; className?: string }) {
  if (coverage.kind !== 'billed') return null;
  return (
    <Link
      data-testid="billed-on"
      to={`/trucking/invoices/${encodeURIComponent(coverage.invoice.invoice_id)}`}
      onClick={(e) => e.stopPropagation()}
      className={`font-mono text-xs font-bold text-fieldiq-text underline underline-offset-2 ${className}`}
    >
      {coverage.invoice.invoice_number || '—'}
    </Link>
  );
}

/** Red, saturated: one BOL on two invoices is double billing. Shared with the BOL list. */
export function MultipleInvoicesBadge({ coverage, className = '' }: { coverage: BolCoverage; className?: string }) {
  const label = multipleInvoicesLabel(coverage);
  if (!label) return null;
  return (
    <span data-testid="billed-on-many" className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 bg-fi-signal-red px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${className}`}>
      {label}
    </span>
  );
}

/** Slate, like an unclassified status: a fact about the data, not an alarm. Shared with the BOL list. */
export function NoInvoiceBadge({ className = '' }: { className?: string }) {
  return (
    <span data-testid="no-invoice" className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${className}`}>
      No invoice
    </span>
  );
}

const refusalOf = (e: unknown): TruckingInvoiceRefusalBody | null => {
  const data = (e as { response?: { data?: unknown } } | null)?.response?.data;
  return data && typeof data === 'object' ? (data as TruckingInvoiceRefusalBody) : null;
};

export default function TruckingInvoiceCard({
  target,
  overlap,
  overlaps,
}: {
  target: InvoiceCardTarget;
  overlap?: unknown;
  /** A move job's linked loads: each one's number and `rental_fee_overlap`. */
  overlaps?: { bol_number: string | null; overlap: unknown }[];
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user as { role?: string } | null)?.role ?? '';
  const canRead = READ_ROLES.includes(role);
  const canWrite = WRITE_ROLES.includes(role);

  // A BOL carries its own coverage; only a move job reads the invoice list.
  const invoicesQuery = useTruckingInvoices(canRead && target.ready && target.kind === 'move_job');
  const fromBol = useCreateInvoiceFromBol();
  const fromMoveJob = useCreateInvoiceFromMoveJob();
  const creating = target.kind === 'load' ? fromBol : fromMoveJob;
  const [refusal, setRefusal] = useState<TruckingInvoiceRefusalBody | null>(null);

  if (!canRead) return null;

  const warnings: string[] = target.kind === 'load'
    ? [feeOverlapWarning(overlap)].filter((w): w is string => !!w)
    : (overlaps ?? []).flatMap((o) => {
        const w = feeOverlapWarning(o.overlap);
        return w ? [`${o.bol_number || '—'}: ${w}`] : [];
      });
  // One gate for a failed list: the failure branch below renders before any
  // reading of `invoices`, so a failed list never reaches "No invoice".
  const failure = target.kind === 'move_job' ? queryLoadFailure(invoicesQuery) : null;
  const invoices = invoicesQuery.data;
  const invoice = target.kind === 'move_job' && invoices ? invoiceForMoveJob(invoices, target.id) : null;

  const generate = async () => {
    setRefusal(null);
    try {
      const created = await creating.mutateAsync(target.id);
      navigate(`/trucking/invoices/${encodeURIComponent(created.id)}`);
    } catch (e) {
      setRefusal(refusalOf(e) ?? { error: (e as { message?: string } | null)?.message || 'The invoice was not created.' });
    }
  };

  const notReady = target.kind === 'load' ? 'A BOL is invoiced once the customer signs it.' : 'A move job is invoiced once its master BOL is finalized.';
  const generateRow = (
    <div className="flex flex-wrap items-center gap-3">
      <NoInvoiceBadge />
      {canWrite ? (
        <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:opacity-50" onClick={generate} disabled={creating.isPending}>
          {creating.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileText className="size-4" aria-hidden="true" />}
          Generate Invoice
        </button>
      ) : (
        <span className="text-xs text-fieldiq-muted">An admin or office staff can generate it.</span>
      )}
    </div>
  );

  return (
    <section data-testid="trucking-invoice-card" aria-labelledby="trucking-invoice-card-title" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-4">
      <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Invoicing</p>
      <h2 id="trucking-invoice-card-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Invoice</h2>

      {warnings.map((w) => (
        <p key={w} data-testid="mob-fee-warning" className="mt-3 flex items-center gap-2 border-l-2 border-fi-signal-amber bg-fi-signal-amber/10 px-2 py-1 text-xs text-fieldiq-text">
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
          {w}
        </p>
      ))}

      <div className="mt-3 text-sm text-fieldiq-text">
        {!target.ready ? (
          <p className="text-fieldiq-muted">{notReady}</p>
        ) : target.kind === 'load' ? (
          target.coverage.kind === 'billed' ? (
            <p data-testid="invoice-found" className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <BilledOnLink coverage={target.coverage} className="text-sm" />
              <StatusPill status={target.coverage.invoice.status} />
              <MultipleInvoicesBadge coverage={target.coverage} />
            </p>
          ) : target.billedOnRental ? (
            <p data-testid="billed-on-rental" className="text-fieldiq-text">Billed on the rental invoice as a haul; not invoiced here.</p>
          ) : target.coverage.kind === 'none' ? (
            generateRow
          ) : (
            <p data-testid="coverage-unknown" className="text-fieldiq-muted">—</p>
          )
        ) : failure ? (
          <LoadFailureNotice error={queryFailureError(invoicesQuery)} kind={failure} subject="the invoices" />
        ) : invoicesQuery.isLoading || !invoices ? (
          <p className="inline-flex items-center gap-2 text-fieldiq-muted">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Checking for an invoice…
          </p>
        ) : invoice ? (
          <p data-testid="invoice-found" className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Link to={`/trucking/invoices/${encodeURIComponent(invoice.id)}`} className="font-bold underline underline-offset-2">
              {invoice.invoice_number}
            </Link>
            <StatusPill status={invoice.status} />
            <span className="tabular-nums">{fmtCurrency(invoice.total, invoice.currency)}</span>
          </p>
        ) : (
          generateRow
        )}

        {refusal && (
          <div role="alert" data-testid="generate-refusal" className="mt-3 space-y-1 border-l-2 border-fi-signal-red bg-fi-signal-red/10 px-2 py-1.5">
            {refusal.problems && refusal.problems.length > 0 ? (
              <ul className="space-y-1">
                {refusal.problems.map((p, i) => <li key={i}>{p.error}</li>)}
              </ul>
            ) : (
              <p>{refusal.error || 'The invoice was not created.'}</p>
            )}
            {(refusal.invoiceId ?? refusal.already_invoiced?.[0]?.invoice_id) && (
              <Link to={`/trucking/invoices/${encodeURIComponent((refusal.invoiceId ?? refusal.already_invoiced?.[0]?.invoice_id)!)}`} className="text-xs font-bold underline underline-offset-2">
                Open the existing invoice
              </Link>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function StatusPill({ status }: { status: unknown }) {
  const skin = truckingInvoiceStatusSkin(status);
  return (
    <span className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${skin.fill}`}>
      {skin.label}
    </span>
  );
}
